import { useState } from "react";
import { Modal } from "flowbite-react";
import ComboBoxFormik from "../../../common/components/form/ComboBoxFormik";
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import ButtonWithLoader from "../../../common/components/ui/loading/ButtonLoading";
import InputFormik from "../../../common/components/form/InputFormik";
import TextareaFormik from "../../renters/components/forms/TextareaFormik";
import { useToastContext } from "../../../common/components/ui/toast/toast.context";
import { maskLicensePlate } from "../../../common/helpers/mask.helpers";
import { IMaintenanceSchedule, MaintenanceScheduleStatus, MaintenanceScheduleType } from "../../../types/maintenanceSchedule.type";
import { deleteMaintenanceSchedule } from "../../../common/services/maintenance-schedule.api";

export type Props = {
	maintenanceSchedule?: IMaintenanceSchedule;
	onRemove: (maintenanceSchedule: IMaintenanceSchedule) => void;
	onClose: () => void;
};

const InsuranceClaimSchema = Yup.object().shape({
	licensePlate: Yup.string().required('Placa é obrigatória'),
	status: Yup.string().required('Status é obrigatório'),
	scheduledDate: Yup.date().required('Data é obrigatória'),
	scheduledTime: Yup.string().required('Hora é obrigatória'),
});

export function ModalDeleteMaintenanceSchedules(props: Props) {
	const [, setIsLoading] = useState(false);
	const { notifyError } = useToastContext();

	const initialValues = (() => {
		if (!props.maintenanceSchedule) return {
			_id: '',
			licensePlate: '',
			typeReview: '',
			companyId: '',
			author: '',
			renterName: '',
			renterPhone: '',
			renterVehicle: '',
			scheduledDate: '',
			scheduledTime: '',
			status: '',
			notes: '',
			updatedAt: null,
			createdAt: new Date(),
		};

		const formattedSchedule = JSON.parse(JSON.stringify(props.maintenanceSchedule));

		if (formattedSchedule.scheduledDate &&
			typeof formattedSchedule.scheduledDate === 'string' &&
			formattedSchedule.scheduledDate.includes('T')) {
			formattedSchedule.scheduledDate = formattedSchedule.scheduledDate.split('T')[0];
		}

		return formattedSchedule;
	})();

	const handleSubmit = async (values) => {
		setIsLoading(true);
		try {
			const result = await deleteMaintenanceSchedule(values._id);

			if (!result.ok)
				return notifyError(result.message || "Erro ao excluir agendamento de manutenção");

			props.onRemove(values);
			props.onClose();
		} catch (error) {
			console.error("Erro ao salvar o agendamento:", error);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Modal
			className='relative text-black'
			show={true}
			onClose={() => props.onClose()}
			size="7x"
			theme={{
				content: {
					base: "relative h-full w-full p-4 md:h-auto",
					inner: "relative rounded-lg bg-white shadow dark:bg-gray-700 max-w-[90%] max-h-[80vh] mx-auto overflow-hidden flex flex-col"
				}
			}}
		>
			<Modal.Header className='text-black'>
				<h2 className='text-black'>
					{initialValues._id ?
						`Agendamento: ${initialValues.renterName ?
							initialValues.renterName.split(' ').map(name =>
								name.charAt(0).toUpperCase() + name.slice(1).toLowerCase()
							).join(' ') : 'Desconhecido'
						}` : "Excluir Agendamento de Manutenção"}
				</h2>
			</Modal.Header>
			<Modal.Body className="min-h-[509px]">
				<Formik
					initialValues={initialValues}
					validationSchema={InsuranceClaimSchema}
					onSubmit={handleSubmit}
				>
					{({ isSubmitting, values }) => (
						<Form>
							<div className="space-y-4 border rounded-lg p-3">

								<div className="resumo">
									<div className="flex flex-col md:flex-row flex-wrap">
										<div className="w-full md:w-1/2 md:pr-2 mb-4 md:mb-0">
											<ComboBoxFormik
												label="Status:"
												name="status"
												value={values.status || ''}
												options={[
													{ value: "", label: "-" },
													{ value: MaintenanceScheduleStatus.Scheduled, label: "Agendado" },
													{ value: MaintenanceScheduleStatus.Completed, label: "Completado" },
													{ value: MaintenanceScheduleStatus.Canceled, label: "Cancelado" },
												]}
												disabled={true}
											/>
										</div>

										<div className="w-full md:w-1/2 md:pr-2 mb-4 md:mb-0">
											<InputFormik
												label="Data:"
												name="scheduledDate"
												type="date"
												value={
													typeof values.scheduledDate === 'object' && values.scheduledDate instanceof Date
														? values.scheduledDate.toISOString().split('T')[0]
														: values.scheduledDate || ''
												}
												className="w-full"
												readonly={true}
											/>

										</div>
										<div className="w-full md:w-1/2 md:pr-2 mb-4 md:mb-0">
											<InputFormik
												label="Hora do evento:"
												name="scheduledTime"
												value={values.scheduledTime || ''}
												type="time"
												className="w-full"
												readonly={true}
											/>
										</div>
										<div className="w-full md:w-1/2 md:pr-2 mb-4 md:mb-0">
											<InputFormik
												label="Placa:"
												value={values.licensePlate || ''}
												name="licensePlate"
												className="w-full"
												readonly={true}
												mask={maskLicensePlate}
											/>
										</div>
										<div className="w-full md:w-1/2 md:pr-2 mb-4 md:mb-0">
											<InputFormik
												label="Nome do cliente:"
												value={values.licensePlate || ''}
												name="renterName"
												className="w-full"
												readonly={true}
											/>
										</div>

										<div className="w-full md:w-1/2 md:pr-2 mb-4 md:mb-0">
											<InputFormik
												label="Telefone do cliente:"
												value={values.licensePlate || ''}
												name="renterPhone"
												className="w-full"
												readonly={true}
											/>
										</div>

										<div className="w-full md:w-1/2 md:pr-2 mb-4 md:mb-0">
											<ComboBoxFormik
												label="Tipo de agendamento:"
												name="typeReview"
												value={values.typeReview || ''}
												options={[
													{ value: MaintenanceScheduleType.Preventive, label: "Revisão Preventiva" },
													{ value: MaintenanceScheduleType.Corrective, label: "Revisão Corretiva" },
													{ value: MaintenanceScheduleType.Other, label: "Outros" },
												]}
												disabled={true}
											/>
										</div>
									</div>

									<div className="flex flex-wrap mb-4 mt-1">
										<div className="w-full">
											<label htmlFor="reportText" className="block mb-2 text-sm font-medium">
												Observações:
											</label>
											<TextareaFormik
												label=""
												name="notes"
												className="w-full min-h-[150px] rounded shadow-md focus:border-white p-2 border border-gray-300"
												placeholder="Descreva informações relevantes..."
												readonly={true}
											/>
										</div>
									</div>
								</div>

								<div className="flex justify-end space-x-3 pt-4">

									<ButtonWithLoader
										type="submit"
										isLoading={isSubmitting}
										disabled={isSubmitting}
									>
										Excluir
									</ButtonWithLoader>
								</div>
							</div>
						</Form>
					)}
				</Formik>
			</Modal.Body>
		</Modal>
	);
}